import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import { Switch, notification } from "antd";
import { useNavigate } from "react-router-dom";
import "./adminPanel.css";

// Panel de administración (rediseño 2026-10-17). Index.js le pasa los datos y
// las acciones que ya existían; aquí solo vive la presentación.

const SECTION_STORAGE_KEY = "xima_admin_panel_section";

const MODE_INFO = {
  free: { label: "Ciencia", sub: "sin dados", icon: "/mode-icons/free.png", color: "#68ccff" },
  cienzar: { label: "Cienzar", sub: "ciencia + azar", icon: "/mode-icons/cienzar.png", color: "#6fff8a" },
  sciencevsazar: {
    label: "Ciencia V/S Azar",
    sub: "uno contra otro",
    icon: "/mode-icons/science-vs-azar.svg",
    color: "#ff7a7a",
  },
  dice: { label: "Azar", sub: "con dados", icon: "/mode-icons/dice.png", color: "#ffe267" },
};
const MODE_ORDER = ["free", "cienzar", "sciencevsazar", "dice"];
// Valores que entiende handleModeChange de Index.js, en el mismo orden.
const CREATE_MODE_VALUES = { free: "ciencia", cienzar: "cienzar", sciencevsazar: "sciencevsazar", dice: "azar" };
const PLAYER_ICONS = { 2: "/mode-icons/players-1v1.png", 3: "/mode-icons/players-1v2.png", 4: "/mode-icons/players-1v3.png" };

const ORDER_STATUS = {
  pendiente_envio: { label: "Pendiente", pill: "org", step: 1 },
  enviado: { label: "Enviado", pill: "blu", step: 2 },
  entregado: { label: "Entregado", pill: "grn", step: 3 },
  cancelado: { label: "Cancelado", pill: "red", step: 0 },
};
const ORDER_FILTERS = [
  { key: "all", label: "Todos" },
  { key: "pendiente_envio", label: "📦 Pendientes" },
  { key: "enviado", label: "🚚 Enviados" },
  { key: "entregado", label: "✅ Entregados" },
  { key: "cancelado", label: "✖ Cancelados" },
];

const AVATAR_COLORS = ["#1565ff", "#c48a00", "#0f9b58", "#8a3bd1", "#b8324a", "#0e7c86", "#3a5080", "#d0601a"];

const ensureArray = (value) => (Array.isArray(value) ? value : []);
const str = (value) => String(value ?? "").trim();
const lower = (value) => str(value).toLowerCase();
const recordId = (record) => str(record?.id || record?._id);

const initialsOf = (name) => {
  const parts = str(name).split(/[\s._@-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts[0][0] + (parts[1]?.[0] || parts[0][1] || "")).toUpperCase();
};
const colorFor = (seed) => {
  const text = str(seed);
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) hash = (hash * 31 + text.charCodeAt(index)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};
const userDisplayName = (user) => {
  if (isGuestUser(user) && !str(user?.firstName)) {
    return Number(user?.guestNumber) > 0 ? `Invitado #${user.guestNumber}` : "Invitado";
  }
  const fullName = `${str(user?.firstName)} ${str(user?.lastName)}`.trim();
  return fullName || str(user?.username) || str(user?.email).split("@")[0] || "-";
};
// Invitado: cuenta de invitado. Registrado: cualquier cuenta que no sea de invitado.
const GUEST_NAME_PATTERN = new RegExp("^(invitado|guest)(\\s|#|\\d|$)", "i");
const isGuestUser = (user) =>
  Boolean(user?.isGuest) || GUEST_NAME_PATTERN.test(str(user?.firstName)) || /@guest[.]/i.test(str(user?.email));
const isRegisteredUser = (user) => !isGuestUser(user);
const isAdminRole = (role) => ["admin", "superuser", "owner"].includes(lower(role));
const fromNow = (value) => (value ? moment(value).fromNow() : "-");
const formatDateTime = (value) => (value ? moment(value).format("D MMM · HH:mm") : "—");
const formatTurn = (minutes) => `${String(Math.max(1, Number(minutes) || 1)).padStart(2, "0")}:00`;
const formatFormat = (memberSize) => `1 vs ${Math.max(1, (Number(memberSize) || 2) - 1)}`;
const formatX = (value) => {
  const amount = Math.max(0, Math.round(Number(value) || 0));
  return amount > 0 ? `${amount} X` : "";
};

const Avatar = ({ name, size }) => (
  <span className="xap-av" style={{ background: colorFor(name), width: size, height: size }}>
    {initialsOf(name)}
  </span>
);

const Pill = ({ tone = "gry", children }) => <span className={`xap-pill xap-p-${tone}`}>{children}</span>;

const ModeDot = ({ modeKey }) => {
  const info = MODE_INFO[modeKey] || MODE_INFO.free;
  return (
    <b className="xap-mdot" style={{ "--m": info.color }}>
      {info.label}
    </b>
  );
};

const Chips = ({ items, value, onChange }) => (
  <div className="xap-chips">
    {items.map((item) => (
      <button
        key={item.key}
        type="button"
        className={`xap-chip ${value === item.key ? "is-on" : ""}`}
        onClick={() => onChange(item.key)}
      >
        {item.label}
        {item.count !== undefined ? <b>{item.count}</b> : null}
      </button>
    ))}
  </div>
);

const Empty = ({ children }) => <div className="xap-empty">{children}</div>;

const AdminPanel = ({
  adminRooms,
  adminUsers,
  adminPuzzles,
  adminIndependentChallenges,
  adminOrders,
  adminLoading,
  adminActionKey,
  currentUserEmail,
  isSiteOwner,
  onRefresh,
  onPurgeRooms,
  onDeleteRoom,
  onDeleteChallenge,
  onDeletePuzzle,
  onSetUserPremium,
  onResetUserPassword,
  onDeleteUser,
  onOrderStatus,
  onOrderNote,
  formatOrderAddress,
  getQuickRemainingMs,
  formatCountdown,
  getTournamentStatus,
  getTournamentName,
  resolveModeKey,
  puzzleOrderById,
  create,
  onEditTournament,
  editTournamentOpen,
}) => {
  const navigate = useNavigate();
  const [section, setSectionState] = useState(() => {
    try {
      return window.localStorage.getItem(SECTION_STORAGE_KEY) || "resumen";
    } catch (error) {
      return "resumen";
    }
  });
  const setSection = useCallback((next) => {
    setSectionState(next);
    try {
      window.localStorage.setItem(SECTION_STORAGE_KEY, next);
    } catch (error) {
      // sin almacenamiento: solo no se recuerda la sección
    }
  }, []);

  const [search, setSearch] = useState("");
  const [orderFilter, setOrderFilter] = useState("pendiente_envio");
  const [orderDetailId, setOrderDetailId] = useState("");
  const [userFilter, setUserFilter] = useState("registered");
  const [userLimit, setUserLimit] = useState(60);
  const [puzzleMode, setPuzzleMode] = useState("free");
  const [puzzleSort, setPuzzleSort] = useState("number");
  const [tournamentFilter, setTournamentFilter] = useState("all");
  const [tournamentModeFilter, setTournamentModeFilter] = useState("all");
  const [tournamentsList, setTournamentsList] = useState([]);
  const [ownerDeletingId, setOwnerDeletingId] = useState("");

  const keyword = lower(search);
  useEffect(() => setSearch(""), [section]);

  const loadTournaments = useCallback(async () => {
    try {
      const response = await axios.get("/tournaments");
      setTournamentsList(ensureArray(response?.data));
    } catch (error) {
      console.warn("Admin: no se pudo cargar la lista de torneos", error);
    }
  }, []);
  useEffect(() => {
    loadTournaments();
    const timer = window.setInterval(loadTournaments, 30000);
    return () => window.clearInterval(timer);
  }, [loadTournaments]);

  // Al cerrar el editor de torneos, recarga la lista para ver los cambios.
  useEffect(() => {
    if (editTournamentOpen === false) loadTournaments();
  }, [editTournamentOpen, loadTournaments]);

  const handleRefresh = useCallback(() => {
    onRefresh?.();
    loadTournaments();
  }, [loadTournaments, onRefresh]);

  /* ---------- Pedidos ---------- */
  const orders = ensureArray(adminOrders);
  const orderCounters = useMemo(() => {
    const counters = { all: orders.length, pendiente_envio: 0, enviado: 0, entregado: 0, cancelado: 0 };
    orders.forEach((order) => {
      const status = lower(order?.status);
      if (counters[status] !== undefined) counters[status] += 1;
    });
    return counters;
  }, [orders]);
  const visibleOrders = useMemo(() => {
    const list = orders.filter((order) => orderFilter === "all" || lower(order?.status) === orderFilter);
    const filtered = keyword
      ? list.filter((order) =>
          [order?.orderCode, recordId(order), order?.userName, order?.userEmail, order?.productName].some((value) =>
            lower(value).includes(keyword)
          )
        )
      : list;
    // Pendientes: el más antiguo primero, que es el que más urge enviar.
    if (orderFilter === "pendiente_envio") {
      return [...filtered].sort(
        (a, b) =>
          new Date(a?.purchaseDate || a?.createdAt || 0).valueOf() -
          new Date(b?.purchaseDate || b?.createdAt || 0).valueOf()
      );
    }
    return filtered;
  }, [keyword, orderFilter, orders]);
  const orderDetail = useMemo(
    () => orders.find((order) => recordId(order) === orderDetailId) || null,
    [orderDetailId, orders]
  );

  /* ---------- Usuarios ---------- */
  const users = ensureArray(adminUsers);
  const weekAgoMs = Date.now() - 7 * 24 * 3600 * 1000;
  const userCounters = useMemo(
    () => ({
      all: users.length,
      registered: users.filter(isRegisteredUser).length,
      guests: users.filter(isGuestUser).length,
      premium: users.filter((user) => user?.isPremium && !isAdminRole(user?.role)).length,
      admins: users.filter((user) => isAdminRole(user?.role)).length,
      active: users.filter((user) => lower(user?.state || "active") === "active").length,
      inactive: users.filter((user) => lower(user?.state || "active") !== "active").length,
      fresh: users.filter((user) => new Date(user?.createdAt || 0).valueOf() >= weekAgoMs).length,
    }),
    [users, weekAgoMs]
  );
  const visibleUsers = useMemo(() => {
    const list = users.filter((user) => {
      if (userFilter === "registered") return isRegisteredUser(user);
      if (userFilter === "guests") return isGuestUser(user);
      if (userFilter === "premium") return user?.isPremium && !isAdminRole(user?.role);
      if (userFilter === "admins") return isAdminRole(user?.role);
      if (userFilter === "active") return lower(user?.state || "active") === "active";
      if (userFilter === "inactive") return lower(user?.state || "active") !== "active";
      if (userFilter === "fresh") return new Date(user?.createdAt || 0).valueOf() >= weekAgoMs;
      return true;
    });
    if (!keyword) return list;
    return list.filter((user) =>
      [userDisplayName(user), user?.email, user?.accountEmail, user?.contactEmail, user?.phone, user?.playerCode].some((value) =>
        lower(value).includes(keyword)
      )
    );
  }, [keyword, userFilter, users, weekAgoMs]);
  useEffect(() => setUserLimit(60), [userFilter, keyword]);

  /* ---------- Puzzles ---------- */
  const puzzles = ensureArray(adminPuzzles);
  const puzzlesByMode = useMemo(() => {
    const groups = { free: [], cienzar: [], sciencevsazar: [], dice: [] };
    puzzles.forEach((puzzle) => {
      const key = resolveModeKey(puzzle?.freeBoxBaseMode || "free");
      (groups[key] || groups.free).push(puzzle);
    });
    return groups;
  }, [puzzles, resolveModeKey]);
  const visiblePuzzles = useMemo(() => {
    const list = [...(puzzlesByMode[puzzleMode] || [])];
    const orderOf = (puzzle) => Number(puzzleOrderById?.[recordId(puzzle)]) || 99999;
    if (puzzleSort === "number") list.sort((a, b) => orderOf(a) - orderOf(b));
    else list.sort((a, b) => new Date(b?.createdAt || 0).valueOf() - new Date(a?.createdAt || 0).valueOf());
    if (!keyword) return list;
    return list.filter(
      (puzzle) =>
        lower(getTournamentName(puzzle?.tournamentName, "Puzzle")).includes(keyword) ||
        String(orderOf(puzzle)) === keyword.replace("#", "")
    );
  }, [getTournamentName, keyword, puzzleMode, puzzleOrderById, puzzleSort, puzzlesByMode]);

  /* ---------- Desafíos ---------- */
  const challenges = ensureArray(adminIndependentChallenges);
  const visibleChallenges = useMemo(
    () =>
      keyword
        ? challenges.filter((challenge) => lower(challenge?.createdBy).includes(keyword))
        : challenges,
    [challenges, keyword]
  );

  /* ---------- Torneos ---------- */
  const tournaments = useMemo(() => {
    const roomsByTournament = new Map();
    ensureArray(adminRooms).forEach((room) => {
      const id = str(room?.tournamentId);
      if (!id) return;
      if (!roomsByTournament.has(id)) roomsByTournament.set(id, []);
      roomsByTournament.get(id).push(room);
    });

    const merged = new Map();
    ensureArray(tournamentsList).forEach((record) => {
      const id = recordId(record);
      if (!id || record?.isPuzzle || record?.isIndependentMatch) return;
      const modeKey = resolveModeKey(record?.mode);
      if (!MODE_INFO[modeKey] || lower(record?.mode).includes("bot") || lower(record?.mode).includes("local")) return;
      const status = getTournamentStatus(record);
      if (status === "finished" && !roomsByTournament.has(id)) return;
      merged.set(id, { id, record, rooms: roomsByTournament.get(id) || [], status });
    });
    roomsByTournament.forEach((rooms, id) => {
      if (merged.has(id)) return;
      const first = rooms[0] || {};
      merged.set(id, {
        id,
        record: {
          _id: id,
          tournamentName: first.tournamentName,
          mode: first.mode,
          memberSize: first.memberSize,
          havingTime: first.havingTime,
          isQuickMatch: first.isQuickMatch,
          quickMatchExpiresAt: first.quickMatchExpiresAt,
          createdBy: first.createdBy,
        },
        rooms,
        status: rooms.some((room) => room?.started) ? "playing" : "waiting",
      });
    });

    return Array.from(merged.values())
      .map((entry) => {
        const remainingMs = entry.record?.isQuickMatch ? getQuickRemainingMs(entry.record) : 0;
        const expired = Boolean(entry.record?.isQuickMatch && entry.record?.quickMatchExpiresAt && remainingMs <= 0);
        const status = expired ? "expired" : entry.status === "playing" ? "playing" : "waiting";
        const connected = entry.rooms.reduce((sum, room) => sum + Math.max(0, Number(room?.connectedPlayers) || 0), 0);
        const registered = ensureArray(entry.record?.users).length;
        return {
          ...entry,
          status,
          remainingMs,
          modeKey: resolveModeKey(entry.record?.mode),
          players: Math.max(connected, registered),
        };
      })
      .sort((a, b) => {
        const rank = { playing: 0, waiting: 1, expired: 2 };
        if (rank[a.status] !== rank[b.status]) return rank[a.status] - rank[b.status];
        return (
          new Date(b.record?.createdAt || b.record?.startingTime || 0).valueOf() -
          new Date(a.record?.createdAt || a.record?.startingTime || 0).valueOf()
        );
      });
  }, [adminRooms, getQuickRemainingMs, getTournamentStatus, resolveModeKey, tournamentsList]);
  const tournamentCounters = useMemo(
    () => ({
      all: tournaments.length,
      playing: tournaments.filter((entry) => entry.status === "playing").length,
      waiting: tournaments.filter((entry) => entry.status === "waiting").length,
      expired: tournaments.filter((entry) => entry.status === "expired").length,
    }),
    [tournaments]
  );
  const visibleTournaments = useMemo(
    () =>
      tournaments.filter((entry) => {
        if (tournamentFilter !== "all" && entry.status !== tournamentFilter) return false;
        if (tournamentModeFilter !== "all" && entry.modeKey !== tournamentModeFilter) return false;
        if (!keyword) return true;
        return [getTournamentName(entry.record?.tournamentName, "Torneo"), entry.id, entry.record?.tournamentSequenceNumber]
          .concat(entry.rooms.map((room) => room?.roomId))
          .some((value) => lower(value).includes(keyword));
      }),
    [getTournamentName, keyword, tournamentFilter, tournamentModeFilter, tournaments]
  );

  const handleOwnerDeleteTournament = useCallback(
    async (entry) => {
      const label = getTournamentName(entry.record?.tournamentName, "Torneo");
      if (!window.confirm(`¿Eliminar el torneo «${label}»? No se puede deshacer.`)) return;
      try {
        setOwnerDeletingId(entry.id);
        await axios.post("/tournaments/owner/tournaments/delete", { tournamentId: entry.id });
        notification.success({ message: "Torneo eliminado", description: label });
        handleRefresh();
      } catch (error) {
        notification.error({
          message: "No se pudo eliminar el torneo",
          description: error?.response?.data?.message || error?.message || "",
        });
      } finally {
        setOwnerDeletingId("");
      }
    },
    [getTournamentName, handleRefresh]
  );

  /* ---------- Navegación ---------- */
  const navItems = [
    { key: "resumen", icon: "📊", label: "Resumen", bg: "#16305e" },
    { key: "pedidos", icon: "📦", label: "Pedidos y envíos", bg: "#4a2a08", badge: orderCounters.pendiente_envio, hot: true },
    { key: "usuarios", icon: "👥", label: "Usuarios", bg: "#123a5c", badge: userCounters.registered },
    { key: "puzzles", icon: "🧩", label: "Puzzles", bg: "#3a2160", badge: puzzles.length },
    { key: "desafios", icon: "⚔️", label: "Desafíos independientes", bg: "#4a1620", badge: challenges.length, hot: true },
    { key: "torneos", icon: "🏆", label: "Torneos", bg: "#3d3308", badge: tournaments.length },
    { key: "crear", icon: "➕", label: "Crear torneo", bg: "#0f3d2a" },
  ];
  const current = navItems.find((item) => item.key === section) || navItems[0];

  const headers = {
    resumen: ["Resumen", "Todo lo importante de XIMA en una sola vista", ""],
    pedidos: ["Pedidos y envíos", "Compras de la tienda que hay que preparar y enviar", "Buscar por orden, usuario o producto…"],
    usuarios: ["Usuarios registrados", `${userCounters.registered} registrados · ${userCounters.guests} invitados · busca, filtra y gestiona Premium`, "Nombre, correo, teléfono o código…"],
    puzzles: ["Puzzles", "Separados por modo de juego · ábrelos o elimínalos", "Buscar puzzle por nombre o número…"],
    desafios: ["Desafíos independientes", "Retos que crean los jugadores fuera de los torneos", "Buscar por creador…"],
    torneos: ["Torneos", "Todos los datos de cada torneo a la vista", "Buscar torneo o sala…"],
    crear: ["Crear torneo", "Multijugador en línea · rellena los pasos de arriba abajo", ""],
  };
  const [title, subtitle, searchPlaceholder] = headers[current.key];

  /* ---------- Secciones ---------- */
  const renderResumen = () => {
    const pendingOrders = orders.filter((order) => lower(order?.status) === "pendiente_envio");
    const oldestPending = [...pendingOrders].sort(
      (a, b) =>
        new Date(a?.purchaseDate || a?.createdAt || 0).valueOf() -
        new Date(b?.purchaseDate || b?.createdAt || 0).valueOf()
    )[0];
    const expiredTournaments = tournaments.filter((entry) => entry.status === "expired");
    const nextWaiting = tournaments
      .filter((entry) => entry.status === "waiting" && entry.record?.startingTime)
      .sort((a, b) => new Date(a.record.startingTime).valueOf() - new Date(b.record.startingTime).valueOf())[0];
    const attention = [];
    if (pendingOrders.length > 0) {
      attention.push({
        key: "orders",
        icon: "📦",
        bg: "#4a2a08",
        title: `${pendingOrders.length} ${pendingOrders.length === 1 ? "pedido sin enviar" : "pedidos sin enviar"}`,
        sub: oldestPending
          ? `El más antiguo ${fromNow(oldestPending.purchaseDate || oldestPending.createdAt)} · ${str(oldestPending.productName) || "Pedido"}`
          : "",
        action: { label: "Ver pedidos", primary: true, onClick: () => { setOrderFilter("pendiente_envio"); setSection("pedidos"); } },
      });
    }
    if (challenges.length > 0) {
      const creators = [...new Set(challenges.map((challenge) => str(challenge?.createdBy)).filter(Boolean))].slice(0, 2);
      attention.push({
        key: "challenges",
        icon: "⚔️",
        bg: "#4a1620",
        title: `${challenges.length} ${challenges.length === 1 ? "desafío independiente esperando" : "desafíos independientes esperando"}`,
        sub: creators.length ? `Creados por ${creators.join(" y ")}` : "",
        action: { label: "Revisar", onClick: () => setSection("desafios") },
      });
    }
    if (nextWaiting) {
      attention.push({
        key: "next",
        icon: "🏆",
        bg: "#3d3308",
        title: `«${getTournamentName(nextWaiting.record?.tournamentName, "Torneo")}» empieza ${moment(nextWaiting.record.startingTime).fromNow()}`,
        sub: `${nextWaiting.players} de ${Math.max(1, Number(nextWaiting.record?.memberSize) || 1)} jugadores`,
        action: { label: "Ver torneos", onClick: () => setSection("torneos") },
      });
    }
    if (expiredTournaments.length > 0) {
      attention.push({
        key: "expired",
        icon: "⏱️",
        bg: "#3d0f16",
        title: `${expiredTournaments.length} ${expiredTournaments.length === 1 ? "sala expirada sin cerrar" : "salas expiradas sin cerrar"}`,
        sub: "Partidas rápidas que ya terminaron su tiempo",
        action: { label: "Ver torneos", danger: true, onClick: () => { setTournamentFilter("expired"); setSection("torneos"); } },
      });
    }

    const activity = [
      ...users.filter(isRegisteredUser).slice(0, 6).map((user) => ({
        key: `u-${recordId(user)}`,
        at: user?.createdAt,
        name: userDisplayName(user),
        text: "se registró",
      })),
      ...orders.slice(0, 6).map((order) => ({
        key: `o-${recordId(order)}`,
        at: order?.purchaseDate || order?.createdAt,
        name: str(order?.userName) || "Alguien",
        text: `compró ${str(order?.productName) || "un producto"}`,
      })),
      ...puzzles.slice(0, 4).map((puzzle) => ({
        key: `p-${recordId(puzzle)}`,
        at: puzzle?.createdAt,
        name: "Nuevo puzzle",
        text: `«${getTournamentName(puzzle?.tournamentName, "Puzzle")}» · ${MODE_INFO[resolveModeKey(puzzle?.freeBoxBaseMode || "free")]?.label}`,
      })),
    ]
      .filter((entry) => entry.at)
      .sort((a, b) => new Date(b.at).valueOf() - new Date(a.at).valueOf())
      .slice(0, 6);

    const kpis = [
      { key: "pedidos", c: "#ff8a00", t: "#ffb35c", l: "📦 Pedidos pendientes", n: orderCounters.pendiente_envio, s: `de ${orders.length} pedidos` },
      { key: "torneos", c: "#ffe267", t: "#ffe267", l: "🏆 Torneos activos", n: tournamentCounters.playing + tournamentCounters.waiting, s: `${tournamentCounters.waiting} esperando jugadores` },
      { key: "desafios", c: "#ff7a7a", t: "#ff98a5", l: "⚔️ Desafíos pendientes", n: challenges.length, s: "independientes" },
      { key: "puzzles", c: "#b48cff", t: "#d2b8ff", l: "🧩 Puzzles", n: puzzles.length, s: "en 4 modos" },
      { key: "usuarios", c: "#29b6ff", t: "#7fd0ff", l: "👥 Registrados", n: userCounters.registered, s: `${userCounters.guests} invitados · +${userCounters.fresh} esta semana` },
      { key: "usuarios-premium", c: "#f5b400", t: "#ffd85a", l: "⭐ Premium", n: userCounters.premium, s: "activos" },
    ];

    return (
      <>
        <div className="xap-kpis">
          {kpis.map((kpi) => (
            <button
              key={kpi.key}
              type="button"
              className="xap-kpi"
              style={{ "--c": kpi.c }}
              onClick={() => {
                if (kpi.key === "usuarios-premium") {
                  setUserFilter("premium");
                  setSection("usuarios");
                } else setSection(kpi.key);
              }}
            >
              <div className="xap-kpi-l">{kpi.l}</div>
              <div className="xap-kpi-n" style={{ color: kpi.t }}>{kpi.n}</div>
              <div className="xap-kpi-s">{kpi.s}</div>
            </button>
          ))}
        </div>
        <div className="xap-grid-2">
          <div className="xap-card">
            <div className="xap-h2"><h2>Necesita tu atención</h2><span className="xap-cnt">{attention.length ? `${attention.length} cosas` : ""}</span></div>
            {attention.length === 0 ? <Empty>Todo al día. No hay nada pendiente. ✨</Empty> : null}
            <ul className="xap-list">
              {attention.map((item) => (
                <li key={item.key}>
                  <span className="xap-ic" style={{ background: item.bg }}>{item.icon}</span>
                  <div className="xap-grow"><b>{item.title}</b><div className="xap-sub">{item.sub}</div></div>
                  <button
                    type="button"
                    className={`xap-btn xap-sm ${item.action.primary ? "xap-pri" : ""} ${item.action.danger ? "xap-red" : ""}`}
                    onClick={item.action.onClick}
                  >
                    {item.action.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="xap-card">
            <div className="xap-h2"><h2>Últimos movimientos</h2></div>
            {activity.length === 0 ? <Empty>Sin movimientos recientes.</Empty> : null}
            <ul className="xap-list">
              {activity.map((entry) => (
                <li key={entry.key}>
                  <Avatar name={entry.name} />
                  <div className="xap-grow"><b>{entry.name}</b> {entry.text}<div className="xap-sub">{fromNow(entry.at)}</div></div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </>
    );
  };

  const renderOrderSteps = (status) => {
    const step = ORDER_STATUS[status]?.step || 0;
    return (
      <span className="xap-steps">
        {[1, 2, 3].map((index) => (
          <i key={index} className={index < step ? "is-ok" : index === step ? "is-on" : ""} />
        ))}
      </span>
    );
  };

  const renderPedidos = () => (
    <div className={`xap-orders ${orderDetail ? "has-detail" : ""}`}>
      <div>
        <div className="xap-h2">
          <Chips
            items={ORDER_FILTERS.map((filter) => ({ ...filter, count: orderCounters[filter.key] || 0 }))}
            value={orderFilter}
            onChange={setOrderFilter}
          />
        </div>
        {visibleOrders.length === 0 ? <Empty>No hay pedidos para este filtro.</Empty> : null}
        {visibleOrders.map((order) => {
          const id = recordId(order);
          const status = lower(order?.status);
          const statusInfo = ORDER_STATUS[status] || { label: status || "-", pill: "gry" };
          const busy = (next) => adminActionKey === `order:status:${id}:${next}`;
          return (
            <div
              key={id}
              className={`xap-order ${status === "pendiente_envio" ? "is-pend" : ""} ${orderDetailId === id ? "is-sel" : ""}`}
              onClick={() => setOrderDetailId(id)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => { if (event.key === "Enter") setOrderDetailId(id); }}
            >
              <div className="xap-thumb">📦</div>
              <div className="xap-o-prod">
                <b>{str(order?.productName) || "Juego de Mesa XIMA"}</b>
                <div className="xap-sub">Orden #{str(order?.orderCode) || id.slice(-6)} · {Math.max(1, Number(order?.quantity) || 1)} {Number(order?.quantity) > 1 ? "unidades" : "unidad"}</div>
              </div>
              <div className="xap-o-user">
                <b>{str(order?.userName) || "-"}</b>
                <div className="xap-sub">{str(order?.userEmail) || "-"}</div>
              </div>
              <div className="xap-o-addr">
                <div>{formatOrderAddress(order) || "-"}</div>
                <div className="xap-sub">{renderOrderSteps(status)} {fromNow(order?.purchaseDate || order?.createdAt)}</div>
              </div>
              <div><Pill tone={statusInfo.pill}>{statusInfo.label}</Pill></div>
              <div className="xap-act" onClick={(event) => event.stopPropagation()}>
                {status === "pendiente_envio" ? (
                  <button type="button" className="xap-btn xap-sm xap-pri" disabled={busy("enviado")} onClick={() => onOrderStatus(order, "enviado")}>
                    🚚 Marcar enviado
                  </button>
                ) : null}
                {status === "enviado" ? (
                  <button type="button" className="xap-btn xap-sm xap-grn" disabled={busy("entregado")} onClick={() => onOrderStatus(order, "entregado")}>
                    ✅ Marcar entregado
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      {orderDetail ? (
        <aside className="xap-drawer">
          <button type="button" className="xap-close" onClick={() => setOrderDetailId("")} aria-label="Cerrar">✕</button>
          <div className="xap-sub xap-cap">Detalle del pedido</div>
          <h3>#{str(orderDetail?.orderCode) || recordId(orderDetail).slice(-6)} · {str(orderDetail?.productName) || "Pedido"}</h3>
          <Pill tone={(ORDER_STATUS[lower(orderDetail?.status)] || {}).pill || "gry"}>
            {(ORDER_STATUS[lower(orderDetail?.status)] || {}).label || str(orderDetail?.status) || "-"}
          </Pill>
          <div className="xap-kv">
            <span>Fecha</span><b>{orderDetail?.purchaseDate || orderDetail?.createdAt ? moment(orderDetail.purchaseDate || orderDetail.createdAt).format("YYYY-MM-DD HH:mm") : "-"}</b>
            <span>Cliente</span><b>{str(orderDetail?.userName) || "-"}</b>
            <span>Correo</span><div>{str(orderDetail?.userEmail) || "-"}</div>
            <span>Teléfono</span><div>{str(orderDetail?.phone) || "-"}</div>
            <span>Cantidad</span><div>{Math.max(1, Number(orderDetail?.quantity) || 1)}</div>
            <span>Pagado</span><div>{Math.max(0, Math.round(Number(orderDetail?.totalPriceX) || 0))} X</div>
            <span>Dirección</span><div>{formatOrderAddress(orderDetail) || "-"}</div>
          </div>
          <div className="xap-sub xap-cap">Seguimiento</div>
          {lower(orderDetail?.status) === "cancelado" ? (
            <div className="xap-tl"><div className="is-cancel">Pedido cancelado</div></div>
          ) : (
            <div className="xap-tl">
              {["Pedido recibido", "Preparando el envío", "Enviado", "Entregado"].map((label, index) => {
                const step = ORDER_STATUS[lower(orderDetail?.status)]?.step || 1;
                const state = index < step ? "is-ok" : index === step ? "is-on" : "";
                return <div key={label} className={state}>{label}</div>;
              })}
            </div>
          )}
          <div className="xap-sub xap-cap">Nota interna</div>
          <div className="xap-note-box">{str(orderDetail?.adminNote) || <span className="xap-sub">Sin nota.</span>}</div>
          <button
            type="button"
            className="xap-btn xap-sm"
            disabled={adminActionKey === `order:note:${recordId(orderDetail)}`}
            onClick={() => onOrderNote(orderDetail)}
          >
            ✏️ {str(orderDetail?.adminNote) ? "Editar nota" : "Añadir nota"}
          </button>
          <div className="xap-drawer-actions">
            {lower(orderDetail?.status) !== "enviado" && lower(orderDetail?.status) !== "entregado" ? (
              <button type="button" className="xap-btn xap-pri xap-grow" onClick={() => onOrderStatus(orderDetail, "enviado")}>🚚 Marcar enviado</button>
            ) : null}
            {lower(orderDetail?.status) !== "entregado" ? (
              <button type="button" className="xap-btn xap-grn xap-grow" onClick={() => onOrderStatus(orderDetail, "entregado")}>✅ Entregado</button>
            ) : null}
            {lower(orderDetail?.status) !== "cancelado" ? (
              <button type="button" className="xap-btn xap-red" onClick={() => onOrderStatus(orderDetail, "cancelado")}>Cancelar</button>
            ) : null}
          </div>
        </aside>
      ) : null}
    </div>
  );

  const renderUsuarios = () => (
    <>
      <div className="xap-h2">
        <Chips
          items={[
            { key: "registered", label: "✉️ Registrados", count: userCounters.registered },
            { key: "guests", label: "👤 Invitados", count: userCounters.guests },
            { key: "all", label: "Todos", count: userCounters.all },
            { key: "premium", label: "⭐ Premium", count: userCounters.premium },
            { key: "admins", label: "🛡️ Admins", count: userCounters.admins },
            { key: "active", label: "🟢 Activos", count: userCounters.active },
            { key: "inactive", label: "⛔ No activos", count: userCounters.inactive },
            { key: "fresh", label: "🆕 Nuevos esta semana", count: userCounters.fresh },
          ]}
          value={userFilter}
          onChange={setUserFilter}
        />
      </div>
      <div className="xap-card xap-table-card">
        <div className="xap-table-scroll">
          <table className="xap-table">
            <thead>
              <tr>
                <th>Jugador</th><th>Contacto</th><th>Código</th><th>Rol</th><th>Estado</th><th>Premium</th><th>Registro</th><th className="xap-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visibleUsers.slice(0, userLimit).map((user) => {
                const email = lower(user?.email);
                const name = userDisplayName(user);
                const isMe = email && email === currentUserEmail;
                const state = lower(user?.state || "active");
                return (
                  <tr key={recordId(user) || email}>
                    <td>
                      <div className="xap-who">
                        <Avatar name={name} />
                        <div>
                          <b>{name}</b>
                          <div className="xap-sub">{str(user?.accountEmail).includes("@") ? str(user.accountEmail) : str(user?.email) || "-"}</div>
                          {str(user?.accountEmail).includes("@") && lower(user?.email) !== lower(user?.accountEmail) ? (
                            <div className="xap-sub">usuario: {str(user?.email)}</div>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>{str(user?.phone) || "—"}</div>
                      <div className="xap-sub">{str(user?.contactEmail) ? `contacto: ${str(user.contactEmail)}` : "—"}</div>
                    </td>
                    <td>{str(user?.playerCode) ? <Pill>{str(user.playerCode)}</Pill> : "—"}</td>
                    <td>{isAdminRole(user?.role) ? <Pill tone="blu">🛡️ {lower(user?.role) === "superuser" ? "Superusuario" : "Admin"}</Pill> : isGuestUser(user) ? <Pill>👤 Invitado</Pill> : "Jugador"}</td>
                    <td>{state === "active" ? <Pill tone="grn">Activo</Pill> : <Pill tone="red">{str(user?.state) || "-"}</Pill>}</td>
                    <td>
                      {isAdminRole(user?.role) ? (
                        <Pill tone="yel">Sin límites</Pill>
                      ) : (
                        <Switch
                          className="xap-premium-switch"
                          checked={Boolean(user?.isPremium)}
                          loading={adminActionKey === `user:premium:${str(user?.id)}`}
                          onChange={(checked) => onSetUserPremium(user, checked)}
                          checkedChildren="Premium"
                          unCheckedChildren="No"
                        />
                      )}
                    </td>
                    <td className="xap-sub">{user?.createdAt ? moment(user.createdAt).format("YYYY-MM-DD") : "—"}</td>
                    <td className="xap-right">
                      <div className="xap-act">
                        {str(user?.playerCode) ? (
                          <button type="button" className="xap-btn xap-sm" onClick={() => navigate(`/game/history?jugador=${encodeURIComponent(str(user.playerCode))}`)}>
                            👁 Historial
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="xap-btn xap-sm"
                          disabled={isMe || adminActionKey === `user:reset:${email}`}
                          onClick={() => onResetUserPassword(user)}
                        >
                          🔑 Resetear clave
                        </button>
                        <button
                          type="button"
                          className="xap-btn xap-sm xap-red"
                          title="Eliminar cuenta"
                          disabled={isMe || adminActionKey === `user:${email}`}
                          onClick={() => onDeleteUser(user)}
                        >
                          🗑
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {visibleUsers.length === 0 ? <Empty>{keyword ? "No hay usuarios que coincidan con la búsqueda." : "No hay usuarios."}</Empty> : null}
        {visibleUsers.length > userLimit ? (
          <div className="xap-more">
            <button type="button" className="xap-btn" onClick={() => setUserLimit((value) => value + 60)}>
              Ver más usuarios ({visibleUsers.length - userLimit} más)
            </button>
          </div>
        ) : null}
      </div>
    </>
  );

  const renderPuzzles = () => {
    const info = MODE_INFO[puzzleMode];
    return (
      <>
        <div className="xap-mtabs">
          {MODE_ORDER.map((key) => {
            const mode = MODE_INFO[key];
            return (
              <button
                key={key}
                type="button"
                className={`xap-mt ${puzzleMode === key ? "is-on" : ""}`}
                style={{ "--m": mode.color }}
                onClick={() => setPuzzleMode(key)}
              >
                <img src={mode.icon} alt="" />
                <div><b>{mode.label}</b><div className="xap-sub">{mode.sub}</div></div>
                <span className="xap-mt-n">{(puzzlesByMode[key] || []).length}</span>
              </button>
            );
          })}
        </div>
        <div className="xap-h2">
          <h2 className="xap-mdot" style={{ "--m": info.color }}>Puzzles de {info.label}</h2>
          <span className="xap-cnt">{visiblePuzzles.length} puzzles</span>
          <div className="xap-r">
            <Chips
              items={[{ key: "number", label: "Por número" }, { key: "newest", label: "Más nuevos" }]}
              value={puzzleSort}
              onChange={setPuzzleSort}
            />
          </div>
        </div>
        {visiblePuzzles.length === 0 ? <Empty>No hay puzzles en este modo.</Empty> : null}
        <div className="xap-pz">
          {visiblePuzzles.map((puzzle) => {
            const id = recordId(puzzle);
            const order = Number(puzzleOrderById?.[id]) || 0;
            return (
              <div key={id} className="xap-pc" style={{ "--m": info.color }}>
                <span className="xap-num">#{order || "-"}</span>
                <div className="xap-pc-art"><img src={info.icon} alt="" /></div>
                <b className="xap-pc-name">{getTournamentName(puzzle?.tournamentName, "Puzzle")}</b>
                <div className="xap-meta">
                  <span>🎯 {Math.max(0, Number(puzzle?.puzzleMoveCount) || 0)} jugadas</span>
                  <span>📅 {fromNow(puzzle?.createdAt)}</span>
                </div>
                <div className="xap-act">
                  <button type="button" className="xap-btn xap-sm" onClick={() => navigate(`/game?openPuzzleId=${encodeURIComponent(id)}`)}>Abrir</button>
                  <button
                    type="button"
                    className="xap-btn xap-sm xap-red xap-push"
                    title="Eliminar puzzle"
                    disabled={!id || adminActionKey === `puzzle:${id}`}
                    onClick={() => onDeletePuzzle(puzzle)}
                  >
                    🗑
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const renderDesafios = () => (
    <>
      {visibleChallenges.length === 0 ? <Empty>No hay desafíos independientes pendientes.</Empty> : null}
      <div className="xap-dc">
        {visibleChallenges.map((challenge) => {
          const id = recordId(challenge);
          const modeKey = resolveModeKey(challenge?.mode);
          const mode = MODE_INFO[modeKey] || MODE_INFO.free;
          const size = Math.max(2, Number(challenge?.memberSize) || 2);
          const joined = Math.max(0, Number(challenge?.joinedPlayersCount) || 0);
          const rivals = Math.max(0, size - 1);
          const joinedRivals = Math.max(0, joined - 1);
          const creator = str(challenge?.createdBy) || "-";
          return (
            <div key={id} className="xap-d" style={{ "--m": mode.color }}>
              <div className="xap-d-hd">
                <b>{str(challenge?.tournamentName) || `Reto de ${creator}`}</b>
                <Pill tone={lower(challenge?.status) === "pendiente" ? "org" : "grn"}>
                  {lower(challenge?.status) === "pendiente" ? `⏳ Esperando ${rivals > 1 ? "rivales" : "rival"}` : str(challenge?.status) || "-"}
                </Pill>
              </div>
              <div className="xap-sub">Creado {fromNow(challenge?.createdAt)}</div>
              <div className="xap-vs">
                <div className="xap-vs-p"><Avatar name={creator} size={48} /><b>{creator}</b><div className="xap-sub">Creador</div></div>
                <span className="xap-vs-x">VS</span>
                <div className="xap-vs-p">
                  <div className="xap-vs-slots">
                    {Array.from({ length: rivals }, (_, index) => (
                      <span key={index} className={index < joinedRivals ? "xap-slot is-in" : "xap-slot"}>{index < joinedRivals ? "✓" : "?"}</span>
                    ))}
                  </div>
                  <b className="xap-sub">{joined} de {size} jugadores</b>
                </div>
              </div>
              <div className="xap-d-row">
                <span>Modo <ModeDot modeKey={modeKey} /></span>
                <span>Formato <b>{formatFormat(size)}</b></span>
                {challenge?.havingTime ? <span>Turno <b>{formatTurn(challenge.havingTime)}</b></span> : null}
              </div>
              <div className="xap-act">
                <button
                  type="button"
                  className="xap-btn xap-sm xap-red xap-push"
                  disabled={adminActionKey === `independent-challenge:${id}`}
                  onClick={() => onDeleteChallenge(challenge)}
                >
                  🗑 Eliminar desafío
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  const renderTorneos = () => (
    <>
      <div className="xap-h2 xap-wrap">
        <Chips
          items={[
            { key: "all", label: "Todos", count: tournamentCounters.all },
            { key: "playing", label: "🟢 En juego", count: tournamentCounters.playing },
            { key: "waiting", label: "🟠 Esperando", count: tournamentCounters.waiting },
            { key: "expired", label: "🔴 Expirados", count: tournamentCounters.expired },
          ]}
          value={tournamentFilter}
          onChange={setTournamentFilter}
        />
        <div className="xap-r">
          <Chips
            items={[{ key: "all", label: "Todos los modos" }].concat(
              MODE_ORDER.map((key) => ({ key, label: MODE_INFO[key].label }))
            )}
            value={tournamentModeFilter}
            onChange={setTournamentModeFilter}
          />
          <button type="button" className="xap-btn xap-sm xap-pri" onClick={() => setSection("crear")}>➕ Crear torneo</button>
        </div>
      </div>
      {visibleTournaments.length === 0 ? <Empty>No hay torneos con este filtro.</Empty> : null}
      {visibleTournaments.map((entry) => {
        const record = entry.record || {};
        const mode = MODE_INFO[entry.modeKey] || MODE_INFO.free;
        const size = Math.max(1, Number(record?.memberSize) || 1);
        const name = getTournamentName(record?.tournamentName, "Torneo");
        const sequence = Number(record?.tournamentSequenceNumber) || 0;
        const fee = Boolean(record?.tournamentPaymentEnabled) || Number(record?.tournamentRewardValue) > 0 ? formatX(record?.tournamentRewardValue) : "";
        const prize = formatX(record?.tournamentWinnerPrizeValue);
        const startMs = new Date(record?.startingTime || 0).valueOf();
        let statusPill;
        if (entry.status === "playing") {
          statusPill = <Pill tone="grn">🟢 En juego{entry.remainingMs > 0 ? ` · termina en ${formatCountdown(entry.remainingMs)}` : ""}</Pill>;
        } else if (entry.status === "expired") {
          statusPill = <Pill tone="red">🔴 Expirado</Pill>;
        } else {
          statusPill = (
            <Pill tone="org">
              🟠 Esperando{startMs > Date.now() ? ` · empieza ${moment(startMs).fromNow()}` : ""}
            </Pill>
          );
        }
        const quickWaitingRoom = entry.rooms.find((room) => room?.isVirtualRoom);
        const realRooms = entry.rooms.filter((room) => !room?.isVirtualRoom);
        return (
          <div key={entry.id} className="xap-tc" style={{ "--m": mode.color }}>
            <div className="xap-tc-hd">
              <img src={mode.icon} alt="" />
              <div className="xap-grow">
                <b className="xap-tc-name">{name}</b>
                <div className="xap-sub">
                  {record?.isQuickMatch ? "Partida rápida" : "Torneo"}
                  {sequence ? ` #${sequence}` : ""}
                  {realRooms.length ? ` · ${realRooms.length} ${realRooms.length === 1 ? "sala" : "salas"}` : ""}
                  {str(record?.createdBy) ? ` · creado por ${str(record.createdBy)}` : ""}
                </div>
              </div>
              <div className="xap-act xap-wrap">
                {statusPill}
                {record?.hasMatchPassword ? <Pill>🔒 Con contraseña</Pill> : null}
                {!record?.isQuickMatch && entry.status !== "expired" ? (
                  <button type="button" className="xap-btn xap-sm" onClick={() => navigate(`/game?openTournamentWaitingId=${encodeURIComponent(entry.id)}`)}>
                    Abrir
                  </button>
                ) : null}
                {onEditTournament && !record?.isQuickMatch && entry.status !== "expired" ? (
                  <button type="button" className="xap-btn xap-sm" onClick={() => onEditTournament(record)}>
                    ✏️ Editar torneo
                  </button>
                ) : null}
                {quickWaitingRoom ? (
                  <button
                    type="button"
                    className="xap-btn xap-sm xap-red"
                    disabled={adminActionKey === `room:${entry.id}:${quickWaitingRoom.roomId}`}
                    onClick={() => onDeleteRoom(quickWaitingRoom)}
                  >
                    Eliminar torneo
                  </button>
                ) : isSiteOwner ? (
                  <button
                    type="button"
                    className="xap-btn xap-sm xap-red"
                    disabled={ownerDeletingId === entry.id}
                    onClick={() => handleOwnerDeleteTournament(entry)}
                  >
                    Eliminar torneo
                  </button>
                ) : null}
              </div>
            </div>
            <div className="xap-facts">
              <div className="xap-f"><span>Modo</span><ModeDot modeKey={entry.modeKey} /></div>
              <div className="xap-f"><span>Formato</span><b>{formatFormat(size)}</b></div>
              <div className="xap-f">
                <span>{entry.status === "playing" ? "Conectados" : "Inscritos"}</span>
                <b>{entry.players} / {size}</b>
                <div className="xap-bar"><i style={{ width: `${Math.min(100, (entry.players / size) * 100)}%` }} /></div>
              </div>
              <div className="xap-f"><span>Tiempo turno</span><b>{formatTurn(record?.havingTime)}</b></div>
              <div className="xap-f"><span>Duración</span><b>{Number(record?.duration) > 0 ? `${Number(record.duration)} min` : record?.quickMatchDurationMinutes ? `${record.quickMatchDurationMinutes} min` : "—"}</b></div>
              <div className="xap-f"><span>Inicio</span><b>{formatDateTime(record?.startingTime || record?.createdAt)}</b></div>
              <div className="xap-f"><span>Entrada</span>{fee ? <b>{fee}</b> : <b className="xap-sub">Gratis</b>}</div>
              <div className="xap-f"><span>Premio</span>{prize ? <b className="xap-gold">🏆 {prize}</b> : <b className="xap-sub">—</b>}</div>
            </div>
            {realRooms.length > 0 ? (
              <div className="xap-rooms">
                {realRooms.map((room) => {
                  const actionId = `room:${entry.id}:${room?.roomId}`;
                  return (
                    <div key={room?.roomId} className="xap-room">
                      <span className="xap-sub">Sala #{str(room?.roomId).slice(-6) || "-"}</span>
                      <span>{room?.started ? "🟢 jugando" : "🟠 esperando"}</span>
                      <span>{Math.max(0, Number(room?.connectedPlayers) || 0)}/{Math.max(1, Number(room?.memberSize) || 1)} conectados</span>
                      {Number(room?.spectators) > 0 ? <span>👁 {room.spectators}</span> : null}
                      <button type="button" className="xap-btn xap-sm xap-red xap-push" disabled={adminActionKey === actionId} onClick={() => onDeleteRoom(room)}>
                        Eliminar sala
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </>
  );

  const renderCrear = () => {
    if (!create) return null;
    const {
      formData,
      panelProgress,
      creating,
      allowedMemberOptions,
      memberOptions,
      timeOptions,
      onModeChange,
      onMemberSize,
      onTurnTime,
      onPasswordEnabled,
      onPassword,
      passwordMaxLength,
      superMinutesInput,
      onSuperMinutes,
      superMinMinutes,
      superMaxMinutes,
      superDefaultMinutes,
      normalizedSuperMinutes,
      canConfigureDuration,
      onPaymentEnabled,
      onRewardValue,
      onPrizeValue,
      maxRewardValue,
      onName,
      onCreationDate,
      startDateValue,
      onStartDate,
      startTimeValue,
      onStartTime,
      canStart,
      onCreate,
      ready,
    } = create;
    const modeKey = resolveModeKey(formData?.mode);
    const modeDone = Boolean(panelProgress?.modeSelected);
    const playersDone = Boolean(panelProgress?.playersSelected);
    const timeDone = Boolean(panelProgress?.timeSelected);
    const mode = MODE_INFO[modeKey] || MODE_INFO.free;
    const name = str(formData?.tournamentName);
    const startLabel =
      startDateValue && startTimeValue
        ? moment(`${startDateValue} ${startTimeValue}`, "YYYY-MM-DD HH:mm").calendar()
        : "al crearlo";
    const missing = [];
    if (!modeDone) missing.push("modo");
    if (!playersDone) missing.push("jugadores");
    if (!timeDone) missing.push("tiempo por turno");
    if (canConfigureDuration && normalizedSuperMinutes < superMinMinutes) missing.push(`duración (${superMinMinutes}+ min)`);

    if (!ready) {
      return <Empty>Preparando el panel de creación…</Empty>;
    }

    return (
      <div className="xap-create">
        <div>
          <div className="xap-step">
            <div className="xap-st"><i className={modeDone ? "is-ok" : ""}>{modeDone ? "✓" : "1"}</i><b>Modo de juego</b><span className="xap-sub xap-push">elige uno de los 4</span></div>
            <div className="xap-opts xap-opts-4">
              {MODE_ORDER.map((key) => {
                const info = MODE_INFO[key];
                const selected = modeDone && modeKey === key;
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={creating}
                    className={`xap-o ${selected ? "is-on" : ""}`}
                    style={{ "--m": info.color }}
                    onClick={() => onModeChange(CREATE_MODE_VALUES[key], { markComplete: true })}
                  >
                    <img src={info.icon} alt="" />
                    <div><span style={{ color: info.color }}>{info.label}</span><div className="xap-sub">{info.sub}</div></div>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="xap-grid-steps">
            <div className={`xap-step ${modeDone ? "" : "is-locked"}`}>
              <div className="xap-st"><i className={playersDone ? "is-ok" : ""}>{playersDone ? "✓" : "2"}</i><b>Jugadores</b></div>
              <div className="xap-opts xap-opts-3">
                {memberOptions.map((value) => {
                  const selected = playersDone && Number(formData?.memberSize) === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      disabled={creating || !modeDone || !allowedMemberOptions.includes(value)}
                      className={`xap-o xap-o-col ${selected ? "is-on" : ""}`}
                      onClick={() => onMemberSize(value)}
                    >
                      {PLAYER_ICONS[value] ? <img src={PLAYER_ICONS[value]} alt="" /> : null}
                      {formatFormat(value)}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className={`xap-step ${modeDone && playersDone ? "" : "is-locked"}`}>
              <div className="xap-st"><i className={timeDone ? "is-ok" : ""}>{timeDone ? "✓" : "3"}</i><b>Tiempo por turno</b></div>
              <div className="xap-opts xap-opts-5">
                {timeOptions.map((value) => {
                  const selected = timeDone && Number(formData?.havingTime) === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      disabled={creating || !modeDone || !playersDone}
                      className={`xap-o xap-o-time ${selected ? "is-on" : ""}`}
                      style={{ "--m": "#be67ff" }}
                      onClick={() => onTurnTime(value)}
                    >
                      {formatTurn(value)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="xap-step">
            <div className="xap-st"><i>4</i><b>Datos del torneo</b></div>
            <div className="xap-fields">
              <label className="xap-fld xap-fld-wide">
                <span>Nombre del torneo</span>
                <input type="text" value={formData?.tournamentName || ""} onChange={(event) => onName(event.target.value)} placeholder="Ej: Copa Ciencia de octubre" />
              </label>
              <label className="xap-fld">
                <span>Fecha de inicio</span>
                <input type="date" className="xima-date-input" value={startDateValue} onChange={(event) => onStartDate(event.target.value)} />
              </label>
              <label className="xap-fld">
                <span>Hora de inicio</span>
                <input type="time" className="xima-date-input" value={startTimeValue} onChange={(event) => onStartTime(event.target.value)} />
              </label>
              {canConfigureDuration ? (
                <label className="xap-fld">
                  <span>Duración del torneo (min)</span>
                  <input
                    type="number"
                    min={superMinMinutes}
                    max={superMaxMinutes}
                    step={1}
                    value={superMinutesInput}
                    onChange={(event) => onSuperMinutes(event.target.value)}
                    placeholder={String(superDefaultMinutes)}
                  />
                </label>
              ) : null}
              <label className="xap-fld">
                <span>Fecha de creación</span>
                <input type="date" className="xima-date-input" value={String(formData?.createdAtDate || "")} onChange={(event) => onCreationDate(event.target.value)} />
              </label>
            </div>
            <div className="xap-sub xap-hint">
              Si dejas el nombre vacío se pone uno automático. La duración mantiene el torneo abierto para nuevas entradas aunque ya haya partidas.
            </div>
          </div>
          <div className="xap-step">
            <div className="xap-st"><i>5</i><b>Acceso y premios</b><span className="xap-sub xap-push">opcional</span></div>
            <div className="xap-fields">
              <div className="xap-tg">
                <Switch checked={Boolean(formData?.hasMatchPassword)} disabled={creating} onChange={(checked) => onPasswordEnabled(Boolean(checked))} />
                <div><b>Contraseña</b><div className="xap-sub">solo entra quien la tenga</div></div>
              </div>
              <label className="xap-fld">
                <span>Contraseña</span>
                <input
                  type="password"
                  maxLength={passwordMaxLength}
                  disabled={!formData?.hasMatchPassword}
                  value={formData?.matchPassword || ""}
                  onChange={(event) => onPassword(event.target.value)}
                  placeholder="Contraseña para unirse"
                />
              </label>
              {canConfigureDuration ? (
                <>
                  <div className="xap-tg">
                    <Switch checked={Boolean(formData?.tournamentPaymentEnabled)} disabled={creating} onChange={(checked) => onPaymentEnabled(Boolean(checked))} />
                    <div><b>Pago para unirse</b><div className="xap-sub">cobra una cuota de entrada</div></div>
                  </div>
                  <div className="xap-fields-2">
                    <label className="xap-fld">
                      <span>Cuota (X)</span>
                      <input
                        type="number"
                        min={1}
                        max={maxRewardValue}
                        step={1}
                        disabled={!formData?.tournamentPaymentEnabled}
                        value={String(formData?.tournamentRewardValue ?? "")}
                        onChange={(event) => onRewardValue(event.target.value)}
                        placeholder="1"
                      />
                    </label>
                    <label className="xap-fld">
                      <span>Premio (X)</span>
                      <input
                        type="number"
                        min={0}
                        max={maxRewardValue}
                        step={1}
                        value={String(formData?.tournamentWinnerPrizeValue ?? "")}
                        onChange={(event) => onPrizeValue(event.target.value)}
                        placeholder="0"
                      />
                    </label>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
        <aside className="xap-sum">
          <h3>Así quedará el torneo</h3>
          <div className="xap-prev" style={{ "--m": mode.color }}>
            <img src={mode.icon} alt="" />
            <div><b>{name || "Nombre automático"}</b><div className="xap-sub">empieza {startLabel}</div></div>
          </div>
          <div className="xap-kv xap-kv-sum">
            <span>Modo</span>{modeDone ? <ModeDot modeKey={modeKey} /> : <b className="xap-sub">—</b>}
            <span>Jugadores</span><b>{playersDone ? formatFormat(formData?.memberSize) : "—"}</b>
            <span>Tiempo por turno</span><b>{timeDone ? formatTurn(formData?.havingTime) : "—"}</b>
            {canConfigureDuration ? (<><span>Duración</span><b>{normalizedSuperMinutes >= superMinMinutes ? `${normalizedSuperMinutes} min` : "—"}</b></>) : null}
            <span>Acceso</span><b>{formData?.hasMatchPassword ? "🔒 Con contraseña" : "🌐 Abierto"}</b>
            {canConfigureDuration ? (
              <>
                <span>Entrada</span><b>{formData?.tournamentPaymentEnabled ? formatX(formData?.tournamentRewardValue) || "—" : "Gratis"}</b>
                <span>Premio del ganador</span><b className="xap-gold">{formatX(formData?.tournamentWinnerPrizeValue) ? `🏆 ${formatX(formData.tournamentWinnerPrizeValue)}` : "—"}</b>
              </>
            ) : null}
          </div>
          <button type="button" className="xap-big" disabled={creating || !canStart} onClick={onCreate}>
            {creating ? "Creando…" : "🏆 Crear torneo"}
          </button>
          <div className="xap-sub xap-center">{missing.length ? `Falta: ${missing.join(", ")}.` : "Todo listo para crear."}</div>
        </aside>
      </div>
    );
  };

  const renderSection = () => {
    switch (current.key) {
      case "pedidos": return renderPedidos();
      case "usuarios": return renderUsuarios();
      case "puzzles": return renderPuzzles();
      case "desafios": return renderDesafios();
      case "torneos": return renderTorneos();
      case "crear": return renderCrear();
      default: return renderResumen();
    }
  };

  useEffect(() => {
    if (current.key === "crear") create?.onEnter?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.key]);

  return (
    <div className="xap-root">
      <aside className="xap-side">
        <img className="xap-logo" src="/ximalogo.png" alt="XIMA" />
        <div className="xap-tag">Panel de administración</div>
        <nav className="xap-nav">
          {navItems.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`xap-nav-item ${item.key === current.key ? "is-on" : ""}`}
              onClick={() => setSection(item.key)}
            >
              <span className="xap-nav-ic" style={{ background: item.bg }}>{item.icon}</span>
              <span className="xap-nav-label">{item.label}</span>
              {item.badge ? <span className={`xap-badge ${item.hot ? "is-hot" : ""}`}>{item.badge}</span> : null}
            </button>
          ))}
        </nav>
        <div className="xap-foot">
          <button type="button" className="xap-btn" disabled={adminLoading} onClick={handleRefresh}>
            {adminLoading ? "Actualizando…" : "↻ Actualizar datos"}
          </button>
          <button type="button" className="xap-btn xap-red" disabled={adminActionKey === "rooms:purge"} onClick={onPurgeRooms}>
            ⟲ Resetear salas
          </button>
        </div>
      </aside>
      <div className="xap-main">
        <header className="xap-top">
          <div className="xap-grow">
            <h1>{title}</h1>
            <div className="xap-crumb">{subtitle}</div>
          </div>
          {searchPlaceholder ? (
            <input className="xap-search" type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={`🔎  ${searchPlaceholder}`} />
          ) : null}
        </header>
        <div className="xap-content">{renderSection()}</div>
      </div>
    </div>
  );
};

export default AdminPanel;
